import { Column, StatColumn, StatProps, TextSkeleton, TextVariant } from "@bridgesplit/ui";
import {
    BsMetaUtil,
    isValidLeverage,
    isWhirlpoolLoop,
    useUnderlyingTokenInfo,
    getHealthStatus,
    LoopExpanded
} from "@bridgesplit/abf-react";
import { formatPercent, Hidable, percentUiToDecimals } from "@bridgesplit/utils";
import { useTransactionFeeStat } from "app/components/transactions";
import { TransactionTypes } from "app/types";
import { COPY } from "app/constants";
import { OracleLogos } from "app/components/oracles";
import { LoopWindType } from "@bridgesplit/abf-sdk";

import { DEFAULT_SLIPPAGE, HealthDetails, HealthText, LiquidationPrice, LoopDisplayType } from "../../common";
import { useLoopHealth, useLoopLiquidationPrice, useWindExecutionDetails } from "./util";
import { useLoopContext } from "../LoopContext";
import { useWindContext } from "./WindContext";
import { ExecutionDetails } from "../execution-detail";
import { ExecutionDetailsLabels } from "../execution-detail/types";
import { WindWhirlpoolStats } from "./WindWhirlpoolStats";

export default function WindStats() {
    const { quote, form, isQuoteFetching, externalQuote, externalQuoteError } = useWindContext();
    const { loopExpanded } = useLoopContext();
    const { oraclePrices } = useUnderlyingTokenInfo(
        loopExpanded?.principalToken.mint && loopExpanded?.collateralToken.mint
            ? [loopExpanded.principalToken.mint, loopExpanded.collateralToken.mint]
            : []
    );

    const principalOracle = oraclePrices?.get(loopExpanded?.principalToken.mint ?? "");
    const collateralOracle = oraclePrices?.get(loopExpanded?.collateralToken.mint ?? "");

    const variant = "body2" as const;

    const health = useLoopHealth();

    const transactionFeeStat = useTransactionFeeStat({
        variant: variant,
        transactionType: TransactionTypes.EarnBorrow
    });

    const liquidationDetails = useLoopLiquidationPrice();

    const { executionDetails, receivedTokenValue } = useWindExecutionDetails({
        externalQuote,
        form,
        principalOracle,
        collateralOracle
    });

    const healthDetails: HealthDetails = {
        health: health?.healthRatio,
        loanHealthStatus: getHealthStatus(health?.ltv, health?.strategyLtv, health?.strategyLqt),
        ltv: health?.ltv
    };

    if (!form.inputAmount || !isValidLeverage(form.multiplier) || !externalQuote || !quote) return null;

    const stats: Hidable<StatProps[]> = [
        {
            caption: "Position size",
            value: quote.leveragedCollateralAmount
        },
        {
            caption: "Health",
            value: <HealthText variant={variant} {...healthDetails} />
        },
        {
            caption: "Loan to value",
            value: formatPercent(health?.ltv)
        },
        {
            caption: "Borrowed",
            value: BsMetaUtil.formatAmount(loopExpanded?.principalToken, quote?.principalAmount)
        },
        {
            caption: "Borrow APY",
            value: formatPercent(quote.bestQuote.apy)
        },
        {
            value: <BorrowLiquidationPrice liquidationDetails={liquidationDetails} variant={variant} />,
            caption: COPY.LIQUIDATION_PRICE_TERM,
            tooltip: COPY.LIQUIDATION_PRICE_TOOLTIP
        },
        {
            caption: "Tick range",
            value: <ClmmStats loopExpanded={loopExpanded} variant={variant} />,
            hide: !isWhirlpoolLoop(loopExpanded)
        },
        transactionFeeStat
    ].filter((stat) => !stat.hide);

    stats.push({
        caption: COPY.ORACLE_TERM_PLURAL,
        value: (
            <OracleLogos
                principalMint={loopExpanded?.loopVault.principalMint}
                collateralMint={loopExpanded?.loopVault.collateralMint}
                variant={variant}
            />
        )
    });

    const labelOverrides = getLabelOverridesForWind();

    const showExecutionDetails =
        !externalQuoteError && (!isWhirlpoolLoop(loopExpanded) || externalQuote.minOutputTokenAmounts);

    return (
        <Column spacing={0.5}>
            <StatColumn variant={variant} loading={isQuoteFetching} stats={stats} />
            {showExecutionDetails && (
                <ExecutionDetails
                    displayType={LoopDisplayType.TokenCostBasis}
                    setDisplayTypeHandler={() => {
                        return null;
                    }}
                    executionDetails={executionDetails}
                    loading={isQuoteFetching}
                    receiveTokenAmount={receivedTokenValue}
                    leftOverPrincipal={0}
                    displayTokenType={LoopWindType.Collateral}
                    slippagePercentDecimals={form.percentSlippageDecimals ?? percentUiToDecimals(DEFAULT_SLIPPAGE)}
                    externalQuote={externalQuote}
                    collateralUsdPrice={collateralOracle?.usdPrice}
                    principalUsdPrice={principalOracle?.usdPrice}
                    principalToken={loopExpanded?.principalToken}
                    collateralToken={loopExpanded?.inputToken}
                    labelOverrides={labelOverrides}
                    textVariant="body2"
                    loopExpanded={loopExpanded}
                />
            )}
        </Column>
    );
}

function BorrowLiquidationPrice({
    variant,
    liquidationDetails
}: {
    variant: TextVariant;
    liquidationDetails: ReturnType<typeof useLoopLiquidationPrice>;
}) {
    const { loopExpanded } = useLoopContext();

    const { liquidationPrice, percentChange } = liquidationDetails;

    if (!liquidationPrice) return <TextSkeleton variant={variant} />;

    return (
        <LiquidationPrice
            variant={variant}
            principal={loopExpanded?.principalToken}
            collateral={loopExpanded?.collateralToken}
            liquidationPrice={liquidationPrice}
            percentChange={percentChange}
        />
    );
}

function getLabelOverridesForWind(): Partial<ExecutionDetailsLabels> {
    return {
        executionImpact: {
            caption: "Execution impact",
            tooltip:
                "Combined worst-case effect of slippage, fees, price impact, and market price when opening a position"
        },
        fairMarketValue: {
            caption: "Fair market value",
            tooltip: "Your contributed collateral plus borrowed principal valued in collateral at oracle prices"
        },
        swapDifference: {
            caption: "Swap difference",
            tooltip:
                "Difference between the oracle-estimated collateral for your borrowed principal and the actual collateral received in the market"
        },
        receiveOnClose: {
            caption: "Position collateral amount",
            tooltip: "Expected minimum total collateral amount on loop open"
        }
    };
}

function ClmmStats({ loopExpanded, variant }: { loopExpanded: LoopExpanded | undefined; variant: TextVariant }) {
    if (!loopExpanded || !isWhirlpoolLoop(loopExpanded)) return null;

    return <WindWhirlpoolStats loopExpanded={loopExpanded} variant={variant} />;
}
